import React, { useState } from "react";
import { format } from "date-fns";
import { Visit } from "../../../interfaces/Visit";
import { message } from "antd";
import { updatePOSDeliveryStatus } from "../../../apis/pos";
import { getIPDLayout, hasCustomIPDLayout } from "../../../utils/IPDLayoutUtil";

interface IPDEntry {
  id?: string | number;
  type: string;
  date: string;
  data: any;
  userId?: number;
  title?: string;
}

interface IPDEntryCardProps {
  entry: IPDEntry;
  visit: Visit | undefined;
  onEdit: () => void;
  onRefresh: () => void;
  onCommentsClick?: (entry: IPDEntry) => void;
}

const IPDEntryCard: React.FC<IPDEntryCardProps> = ({
  entry,
  visit,
  onEdit,
  onRefresh,
  onCommentsClick,
}) => {
  // onRefresh is used in parent; entry deletion is handled elsewhere.

  const [isUpdatingPharmacyDelivery, setIsUpdatingPharmacyDelivery] =
    useState(false);

  const isEmptyValue = (value: any) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string") return value.trim() === "";
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "object") return Object.keys(value).length === 0;
    return false;
  };

  const formatLabel = (key: string) =>
    key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\s+/g, " ")
      .trim();

  const formatPrimitive = (key: string, value: any) => {
    if (typeof value === "string") {
      const lowerKey = key.toLowerCase();
      const looksLikeDate =
        lowerKey.includes("date") || lowerKey.includes("time");
      const parsed = new Date(value);
      if (looksLikeDate && !isNaN(parsed.getTime())) {
        // For pure *date* fields like admissionDate, show only the date.
        // For keys that include time (or generic datetime strings), show date + time.
        const pattern = lowerKey.includes("date") && !lowerKey.includes("time")
          ? "dd MMM yyyy"
          : "dd MMM yyyy, HH:mm";
        return format(parsed, pattern);
      }
    }
    return String(value);
  };

  const isLargeValue = (value: any) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim().length > 120;
    if (Array.isArray(value)) return value.length > 4;
    if (typeof value === "object") return Object.keys(value).length > 4;
    return false;
  };

  const isMediumValue = (value: any) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim().length > 60;
    if (Array.isArray(value)) return value.length > 2;
    if (typeof value === "object") return Object.keys(value).length > 2;
    return false;
  };

  const getStatusLabel = (status: string | null | undefined) => {
    if (!status) return "-";
    return status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getDeliveryStatusClasses = (status: string | null | undefined) => {
    const normalized = (status || "").toLowerCase();
    switch (normalized) {
      case "completed":
      case "paid":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
      case "rejected":
        return "bg-red-100 text-red-800";
      case "dispatched":
        return "bg-blue-100 text-blue-800";
      case "received":
        return "bg-purple-100 text-purple-800";
      case "partially_paid":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const draftJsToText = (raw: any): string => {
    if (!raw) return "";
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (Array.isArray(parsed?.blocks)) {
        return parsed.blocks
          .map((b: any) => b.text ?? "")
          .filter(Boolean)
          .join("\n");
      }
    } catch {}
    return typeof raw === "string" ? raw : "";
  };

  const renderOTDetails = () => {
    const d = entry?.data || {};

    const procedures: any[] =
      Array.isArray(d.procedures) && d.procedures.length > 0
        ? d.procedures
        : d.procedureName
        ? [{ name: d.procedureName }]
        : [];

    const dateRaw = d.procedureDate ?? d.procedure_date;
    const time = d.procedureTime ?? d.procedure_time;
    const status = d.status;

    const surgeon1     = d.surgeon1Name     ?? d.surgeon1_name     ?? null;
    const surgeon2     = d.surgeon2Name     ?? d.surgeon2_name     ?? null;
    const anaesthetist = d.anaesthetistName ?? d.anaesthetist_name ?? null;
    const assistant    = d.assistantName    ?? d.assistant_name    ?? null;

    const operationDetails = draftJsToText(d.operationDetails ?? d.operation_details);
    const findings     = d.findings;
    const bloodLoss    = d.bloodLossMl ?? d.blood_loss_ml;
    const complications = d.complications;
    const implants     = d.implants;
    const notes        = d.notes;

    const statusColors: Record<string, string> = {
      scheduled: "bg-blue-100 text-blue-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    };

    const formattedDate = (() => {
      if (!dateRaw) return "—";
      // Parse YYYY-MM-DD without timezone shift
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateRaw)) {
        const [y, m, day] = dateRaw.split("-").map(Number);
        return format(new Date(y, m - 1, day), "dd MMM yyyy");
      }
      const p = new Date(dateRaw);
      return isNaN(p.getTime()) ? dateRaw : format(p, "dd MMM yyyy");
    })();

    return (
      <div className="space-y-4">
        {/* Procedure(s) + date/time + status */}
        <div className="flex flex-wrap gap-6 items-start">
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase mb-1">
              Procedure(s)
            </div>
            {procedures.length > 0 ? (
              procedures.map((p: any, i: number) => (
                <div key={i} className="text-sm font-semibold text-gray-900">
                  {p.name}
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500">—</div>
            )}
          </div>
          <div>
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase mb-1">
              Date &amp; Time
            </div>
            <div className="text-sm text-gray-800">
              {formattedDate}
              {time ? ` at ${time}` : ""}
            </div>
          </div>
          {status && (
            <div className="pt-0.5">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                  statusColors[status] ?? "bg-gray-100 text-gray-700"
                }`}
              >
                {status}
              </span>
            </div>
          )}
        </div>

        {/* Surgical team */}
        {(surgeon1 || surgeon2 || anaesthetist || assistant) && (
          <div>
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase mb-2">
              Surgical Team
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {surgeon1 && (
                <div>
                  <div className="text-xs text-gray-500">Surgeon 1</div>
                  <div className="text-sm font-medium text-gray-800">{surgeon1}</div>
                </div>
              )}
              {surgeon2 && (
                <div>
                  <div className="text-xs text-gray-500">Surgeon 2</div>
                  <div className="text-sm font-medium text-gray-800">{surgeon2}</div>
                </div>
              )}
              {anaesthetist && (
                <div>
                  <div className="text-xs text-gray-500">Anaesthetist</div>
                  <div className="text-sm font-medium text-gray-800">{anaesthetist}</div>
                </div>
              )}
              {assistant && (
                <div>
                  <div className="text-xs text-gray-500">Assistant</div>
                  <div className="text-sm font-medium text-gray-800">{assistant}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Operation details (rich text → plain) */}
        {operationDetails && (
          <div>
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase mb-1">
              Operation Details
            </div>
            <div className="text-sm text-gray-800 whitespace-pre-line bg-gray-50 rounded p-2">
              {operationDetails}
            </div>
          </div>
        )}

        {/* Clinical fields */}
        {(findings || bloodLoss || complications || implants || notes) && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-3 border-t border-gray-100">
            {findings && (
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase">Findings</div>
                <div className="text-sm text-gray-800 mt-0.5">{findings}</div>
              </div>
            )}
            {bloodLoss && (
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase">Blood Loss</div>
                <div className="text-sm text-gray-800 mt-0.5">{bloodLoss} ml</div>
              </div>
            )}
            {complications && (
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase">Complications</div>
                <div className="text-sm text-gray-800 mt-0.5">{complications}</div>
              </div>
            )}
            {implants && (
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase">Implants</div>
                <div className="text-sm text-gray-800 mt-0.5">{implants}</div>
              </div>
            )}
            {notes && (
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase">Notes</div>
                <div className="text-sm text-gray-800 mt-0.5">{notes}</div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderLabRequestDetails = () => {
    const data = entry?.data || {};
    const labRequest = data.lab_request || data.labRequest || null;

    if (!labRequest) {
      return (
        <div className="text-sm text-gray-500">
          No lab request details available.
        </div>
      );
    }

    const labTest = labRequest.lab_test || labRequest.labTest || {};
    const testName = labTest.name || "-";
    const testCode = labTest.code ? `(${labTest.code})` : "";
    const orderNumber = labRequest.order_number || labRequest.orderNumber || "-";
    const sampleId = labRequest.sample_id || labRequest.sampleId || "-";
    const status = labRequest.status || "-";
    const priority = labRequest.priority || "-";
    const charge = labRequest.charge != null ? labRequest.charge : "-";
    const referredBy = labRequest.referred_by || labRequest.referredBy || "-";
    const notes =
      data.notes || labRequest.notes || "";

    return (
      <div className="space-y-3">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Order Number
            </div>
            <div className="mt-1 text-sm text-gray-800">{orderNumber}</div>
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Sample ID
            </div>
            <div className="mt-1 text-sm text-gray-800">{sampleId}</div>
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Status
            </div>
            <div className="mt-1 text-sm text-gray-800 capitalize">{status}</div>
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Priority
            </div>
            <div className="mt-1 text-sm text-gray-800 capitalize">{priority}</div>
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Charge
            </div>
            <div className="mt-1 text-sm text-gray-800">{charge}</div>
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Referred By
            </div>
            <div className="mt-1 text-sm text-gray-800">{referredBy}</div>
          </div>
        </div>
        <div className="pt-2 border-t border-gray-100">
          <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
            Lab Test
          </div>
          <div className="mt-1 text-sm font-medium text-gray-800">
            {testName} {testCode}
          </div>
        </div>
        {notes && (
          <div className="pt-2 border-t border-gray-100">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Notes
            </div>
            <div className="mt-1 text-sm text-gray-800">{notes}</div>
          </div>
        )}
      </div>
    );
  };

  const renderPharmacyRequestDetails = () => {
    const data = entry?.data || {};
    const pharmacyRequest =
      data.pharmacy_request || data.pharmacyRequest || null;

    if (!pharmacyRequest) {
      return (
        <div className="text-sm text-gray-500">
          No pharmacy request details available.
        </div>
      );
    }

    const saleId =
      pharmacyRequest.id ||
      pharmacyRequest.sale_id ||
      pharmacyRequest.saleId ||
      data.pharmacy_sale_id ||
      data.pharmacySaleId;

    const saleNumber =
      pharmacyRequest.sale_number ||
      pharmacyRequest.saleNumber ||
      data.pharmacy_sale_id;
    const doctorName =
      pharmacyRequest.doctor?.name ||
      data.doctor?.name ||
      data.doctor_name ||
      "";
    const status = pharmacyRequest.status || data.status;
    const deliveryStatusRaw =
      pharmacyRequest.delivery_status ??
      pharmacyRequest.deliveryStatus ??
      data.delivery_status ??
      data.deliveryStatus;
    const deliveryStatus = deliveryStatusRaw || null;
    const items = pharmacyRequest.items || [];

    return (
      <div className="space-y-3">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Sale Number
            </div>
            <div className="mt-1 text-sm text-gray-800">
              {saleNumber || "-"}
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Status
            </div>
            <div className="mt-1 text-sm text-gray-800 capitalize">
              {status || "-"}
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Delivery Status
            </div>
            <div className="flex gap-2 items-center mt-1">
              <span
                className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full capitalize ${getDeliveryStatusClasses(
                  deliveryStatus || "pending",
                )}`}
              >
                {getStatusLabel(deliveryStatus || "pending")}
              </span>
            </div>
          </div>

        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {items && items.length > 0 && (
            <div>
              <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Items
              </div>
              <ul className="mt-1 text-sm list-disc list-inside text-gray-800">
                {items.map((item: any) => {
                  const pharmacyItem =
                    item.pharmacy_item || item.pharmacyItem || {};
                  const name = pharmacyItem.name || "-";
                  const sku = pharmacyItem.sku;
                  const quantity = item.quantity;
                  return (
                    <li key={item.id}>
                      {quantity} x {name}
                      {sku ? ` (${sku})` : ""}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {data.notes && (
            <div className="pt-2 border-t border-gray-100">
              <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Notes
              </div>
              <div className="mt-1 text-sm text-gray-800">{data.notes}</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getColumnClass = (itemCount: number) => {
    if (itemCount <= 3) return "grid-cols-3";
    if (itemCount <= 6) return "grid-cols-2";
    return "grid-cols-1";
  };

  const renderValue = (key: string, value: any) => {
    if (Array.isArray(value)) {
      return (
        <ul className="text-sm list-disc list-inside text-gray-700">
          {value.map((item, idx) => (
            <li key={idx}>{renderValue(key, item)}</li>
          ))}
        </ul>
      );
    }

    if (typeof value === "object" && value !== null) {
      const label =
        value.label || value.name || value.title || value.value || value.id;
      if (
        label !== undefined &&
        label !== null &&
        String(label).trim() !== ""
      ) {
        return <span className="text-sm text-gray-800">{String(label)}</span>;
      }

      const entries = Object.entries(value).filter(([, v]) => !isEmptyValue(v));

      if (entries.length === 0) return null;

      const columnClass = getColumnClass(entries.length);

      return (
        <div className={`grid gap-3 ${columnClass}`}>
          {entries.map(([childKey, childValue]) => (
            <div key={childKey} className="min-w-0 text-sm text-gray-700">
              <span className="text-gray-500">{formatLabel(childKey)}: </span>
              {renderValue(childKey, childValue)}
            </div>
          ))}
        </div>
      );
    }

    return (
      <span className="text-sm text-gray-800">
        {formatPrimitive(key, value)}
      </span>
    );
  };

  const renderDoctorRoundsDetails = () => {
    const d = entry?.data || {};
    const vitals = d.vitals ?? {};
    const observations: string = d.observations ?? "";
    const plan: string = d.plan ?? "";
    const notes: string = d.notes ?? "";
    const meds: any[] = Array.isArray(d.medicationsUpdated)
      ? d.medicationsUpdated
      : [];

    const vitalItems = [
      { label: "BP", value: vitals.bp, unit: "mmHg" },
      { label: "Pulse", value: vitals.pulse, unit: "bpm" },
      { label: "Temp", value: vitals.temp, unit: "°C" },
      { label: "SpO2", value: vitals.spo2, unit: "%" },
    ].filter((v) => v.value);

    return (
      <div className="space-y-3">
        {/* Vitals */}
        {vitalItems.length > 0 && (
          <div className="flex items-center gap-4 flex-wrap">
            {vitalItems.map((v) => (
              <div key={v.label} className="flex items-baseline gap-1">
                <span className="text-xs text-gray-400">{v.label}</span>
                <span className="text-sm font-semibold text-gray-800">{v.value} {v.unit}</span>
              </div>
            ))}
          </div>
        )}

        {/* Observations + Plan */}
        {(observations || plan) && (
          <div className="grid grid-cols-2 gap-4">
            {observations && (
              <div>
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Observations</div>
                <div className="text-sm text-gray-800 whitespace-pre-line">{observations}</div>
              </div>
            )}
            {plan && (
              <div>
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Plan</div>
                <div className="text-sm text-gray-800 whitespace-pre-line">{plan}</div>
              </div>
            )}
          </div>
        )}

        {/* Medications changed this round */}
        {meds.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Medications</div>
            <div className="space-y-1">
              {meds.map((m: any, i: number) => {
                const action = m.action ?? "updated";
                const color =
                  action === "added" ? "bg-green-50 border-green-200 text-green-700" :
                  action === "stopped" ? "bg-red-50 border-red-200 text-red-500" :
                  "bg-gray-50 border-gray-200 text-gray-700";
                const dose = m.instructions?.dose ?? m.instructions?.power ?? "";
                const route = m.instructions?.route ?? "";
                const freq = m.instructions?.frequency ?? "";
                const detail = [dose, route, freq].filter(Boolean).join(" · ");
                return (
                  <div key={i} className={`flex items-center gap-2 text-xs px-2 py-1 rounded border ${color}`}>
                    <span className="font-medium capitalize">{action}</span>
                    <span className="font-semibold">{m.medicineName ?? m.name}</span>
                    {detail && <span className="opacity-70">· {detail}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Notes */}
        {notes && (
          <div className="text-sm text-gray-600 border-t border-gray-100 pt-2">{notes}</div>
        )}

        {/* Comments link */}
        {onCommentsClick && (
          <div className="border-t border-gray-100 pt-2">
            <button
              type="button"
              onClick={() => onCommentsClick(entry)}
              className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-900"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              {(entry?.data?.comments) ? "View / Edit comments" : "Add comments"}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderCustomLayoutDetails = () => {
    const d = entry?.data || {};
    const flowTypeId = (visit as any)?.clinicVisitTypeId ?? (visit as any)?.meta?.ipdFlowTypeId;
    const entryType = entry.type as any;

    // Try to get the layout definition so we can use proper field labels
    let gridItems: any[] = [];
    if (flowTypeId && hasCustomIPDLayout(flowTypeId, entryType)) {
      const layout = getIPDLayout(flowTypeId, entryType);
      gridItems = layout?.gridItems ?? [];
    }

    // Build ordered label→value pairs from the layout definition
    const items: { label: string; value: any; key: string; component: string }[] = [];

    if (gridItems.length > 0) {
      // Sort by y then x so display order matches the form layout
      const sorted = [...gridItems].sort((a, b) =>
        a.y !== b.y ? a.y - b.y : a.x - b.x
      );
      for (const item of sorted) {
        const key = item.data?.key ?? `field_${item.id}`;
        const label = item.data?.label ?? item.label ?? key;
        const value = d[key];
        if (value !== null && value !== undefined && value !== "") {
          items.push({ label, value, key, component: item.component });
        }
      }
    } else {
      // No layout definition available — fall back to raw key/value pairs
      for (const key of Object.keys(d)) {
        if (!isEmptyValue(d[key])) {
          items.push({ label: formatLabel(key), value: d[key], key, component: "AmsaInput" });
        }
      }
    }

    if (items.length === 0) {
      return <div className="text-sm text-gray-400">No details available.</div>;
    }

    const formatCustomValue = (value: any, component: string): string => {
      if (value === null || value === undefined) return "—";
      if (typeof value === "boolean") return value ? "Yes" : "No";
      if (Array.isArray(value)) return value.map((v: any) => v?.label ?? v?.name ?? String(v)).join(", ");
      if (typeof value === "object") return value?.label ?? value?.name ?? value?.value ?? JSON.stringify(value);
      return String(value);
    };

    return (
      <div className="grid grid-cols-2 gap-x-8 gap-y-3 md:grid-cols-3 lg:grid-cols-4">
        {items.map((item) => (
          <div key={item.key} className="min-w-0">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
              {item.label}
            </div>
            <div className="text-sm text-gray-800 whitespace-pre-line break-words">
              {formatCustomValue(item.value, item.component)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderRoomTransferDetails = () => {
    const d = entry?.data || {};
    const fromRoomName = d.from_room_name ?? d.fromRoomName;
    const toRoomName   = d.to_room_name   ?? d.toRoomName;
    const reason      = d.reason        ?? "";
    const billedRate  = d.billed_rate_per_day ?? d.billedRatePerDay;
    const defaultRate = d.room_type_price_per_day ?? d.roomTypePricePerDay;
    const hasMismatch = billedRate !== undefined && billedRate !== null && defaultRate !== undefined && defaultRate !== null && Number(billedRate) !== Number(defaultRate);

    const transferDateRaw = d.transfer_date ?? d.transferDate;
    const transferDateStr = (() => {
      if (!transferDateRaw) return null;
      try { return format(new Date(transferDateRaw), "dd MMM yyyy, HH:mm"); } catch { return null; }
    })();

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm">
            <span className="text-gray-500 text-xs">From</span>
            <span className="font-medium text-gray-800">{fromRoomName ?? "—"}</span>
            <span className="text-gray-400">→</span>
            <span className="text-xs text-gray-500">To</span>
            <span className="font-medium text-gray-800">{toRoomName ?? "—"}</span>
          </div>
          {transferDateStr && (
            <span className="text-xs text-gray-400">{transferDateStr}</span>
          )}
        </div>

        {/* Billing rate row */}
        <div className="flex items-center gap-3 flex-wrap">
          {billedRate !== undefined && billedRate !== null && (
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border ${
              hasMismatch
                ? "bg-amber-50 border-amber-300 text-amber-700"
                : "bg-gray-50 border-gray-200 text-gray-700"
            }`}>
              <span>Billed rate: ₹{billedRate}/day</span>
              {hasMismatch && (
                <span className="opacity-70">(room default: ₹{defaultRate}/day)</span>
              )}
            </div>
          )}
          {hasMismatch && (
            <span className="text-xs text-amber-600 font-medium">⚠ Rate override applied</span>
          )}
        </div>

        {reason && (
          <div className="text-sm text-gray-600 border-t border-gray-100 pt-2">{reason}</div>
        )}
      </div>
    );
  };

  const renderNursingDetails = () => {
    const d = entry?.data || {};
    const shift: string = d.shift ?? "";
    const vitals = d.vitals ?? {};
    const notes: string = d.notes ?? "";
    const meds: any[] = Array.isArray(d.medicationsAdministered) ? d.medicationsAdministered : [];

    const shiftLabel = shift
      ? { morning: "Morning (6am–2pm)", evening: "Evening (2pm–10pm)", night: "Night (10pm–6am)" }[shift] ?? shift
      : null;

    const vitalItems = [
      { label: "BP", value: vitals.bp, unit: "mmHg" },
      { label: "Pulse", value: vitals.pulse, unit: "bpm" },
      { label: "Temp", value: vitals.temp, unit: "°C" },
      { label: "SpO2", value: vitals.spo2, unit: "%" },
    ].filter((v) => v.value);

    const givenMeds = meds.filter((m) => m.given);
    const notGivenMeds = meds.filter((m) => !m.given);

    return (
      <div className="space-y-3">
        {/* Shift + vitals row */}
        <div className="flex items-center gap-4 flex-wrap">
          {shiftLabel && (
            <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium border border-indigo-200">
              {shiftLabel}
            </span>
          )}
          {vitalItems.map((v) => (
            <div key={v.label} className="flex items-baseline gap-1">
              <span className="text-xs text-gray-400">{v.label}</span>
              <span className="text-sm font-semibold text-gray-800">{v.value} {v.unit}</span>
            </div>
          ))}
          {vitalItems.length === 0 && !shiftLabel && (
            <span className="text-xs text-gray-400">No vitals recorded</span>
          )}
        </div>

        {/* Medications administered */}
        {meds.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Medications Administered</div>
            <div className="space-y-1">
              {givenMeds.map((m, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className="w-3.5 h-3.5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 text-xs leading-none">✓</span>
                  </span>
                  <span className="text-gray-800 font-medium">{m.medicineName}</span>
                  {m.instructions?.route && <span className="text-gray-400 text-xs">· {m.instructions.route}</span>}
                </div>
              ))}
              {notGivenMeds.map((m, i) => (
                <div key={i} className="flex items-center gap-2 text-sm opacity-50">
                  <span className="w-3.5 h-3.5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-gray-400 text-xs leading-none">–</span>
                  </span>
                  <span className="text-gray-500 line-through">{m.medicineName}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {notes && (
          <div className="text-sm text-gray-600 border-t border-gray-100 pt-2">{notes}</div>
        )}
      </div>
    );
  };

  const renderEntryContent = () => {
    const data = entry?.data || {};
    // Exclude 'comments' from main grid when we show it as a clickable row for doctor_rounds
    // Custom rendering for specific entry types
    if (entry.type === "admission") {
      const toDisplayString = (v: any): string => {
        if (v == null || v === "") return "";
        if (typeof v === "string" || typeof v === "number") return String(v);
        if (typeof v === "object" && v !== null) {
          const name = v.name ?? v.label ?? v.title;
          if (name != null && String(name).trim() !== "") return String(name);
          return String(v.id ?? v.value ?? "");
        }
        return String(v);
      };

      const admissionDateRaw = data.admissionDate;
      const roomRaw =
        data.roomName ??
        data.room ?? data.roomNumber ??
        (visit as any)?.meta?.roomName ??
        (visit as any)?.meta?.room;
      const bedRaw = data.bedName ?? data.bedId ?? data.bed ?? (visit as any)?.meta?.bedName ?? (visit as any)?.meta?.bed;
      const chiefComplaint = toDisplayString(
        data.chiefComplaint ??
        (visit as any)?.chiefComplaint
      );
      const insuranceProviderName = toDisplayString(
        data.insuranceProvider ??
        data.insuranceProviderName ??
        (visit as any)?.meta?.insuranceProvider

      );
      const policyNumber = toDisplayString(
        data.policyNumber ??

        (visit as any)?.meta?.policyNumber
      );
      const paymentType = toDisplayString(
        data.paymentType ??
        (visit as any)?.meta?.paymentType
      );
      const coverageTypeRaw =
        data.coverageType ?? (visit as any)?.meta?.coverageType;
      const coverageTypeDisplay =
        coverageTypeRaw === "reimbursement"
          ? "Reimbursement"
          : coverageTypeRaw === "cashless"
            ? "Cashless"
            : toDisplayString(coverageTypeRaw);

      const formattedAdmissionDate = (() => {
        if (!admissionDateRaw) return "";
        const parsed = new Date(admissionDateRaw);
        if (isNaN(parsed.getTime())) return toDisplayString(admissionDateRaw);
        return format(parsed, "dd/MM/yyyy");
      })();

      const roomName = toDisplayString(roomRaw);
      const bedName = toDisplayString(bedRaw);

      const items: { label: string; value: string }[] = [
        { label: "Admission Date", value: formattedAdmissionDate },
        { label: "Room Number", value: roomName },
        // { label: "Bed", value: bedName },
        { label: "Chief Complaint", value: chiefComplaint },
        { label: "Insurance Provider", value: insuranceProviderName },
        { label: "Policy Number", value: policyNumber },
        { label: "Coverage Type", value: coverageTypeDisplay },
        { label: "Payment Type", value: paymentType },
      ].filter((item) => item.value && item.value !== "undefined");

      if (items.length === 0) {
        return <div className="text-sm text-gray-500">No details available.</div>;
      }

      return (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {items.map((item) => (
            <div key={item.label} className="min-w-0">
              <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                {item.label}
              </div>
              <div className="mt-1 text-sm text-gray-800 break-words">
                {item.value}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (entry.type === "pharmacy_request") {
      return renderPharmacyRequestDetails();
    }
    if (entry.type === "lab_request") {
      return renderLabRequestDetails();
    }
    if (entry.type === "ot") {
      return renderOTDetails();
    }
    if (entry.type === "nursing") {
      return renderNursingDetails();
    }
    if (entry.type === "doctor_rounds") {
      return renderDoctorRoundsDetails();
    }
    if (entry.type === "room_transfer") {
      return renderRoomTransferDetails();
    }

    // For assessment and other custom-layout entry types, try custom layout first
    const flowTypeId = (visit as any)?.clinicVisitTypeId ?? (visit as any)?.meta?.ipdFlowTypeId;
    if (flowTypeId && hasCustomIPDLayout(flowTypeId, entry.type as any)) {
      return renderCustomLayoutDetails();
    }

    const keys = Object.keys(data).filter(
      (key) => key !== "comments" && !isEmptyValue(data[key]),
    );

    if (keys.length === 0) {
      return <div className="text-sm text-gray-500">No details available.</div>;
    }

    const columnClass = getColumnClass(keys.length);

    return (
      <>
        {keys.length > 0 && (
          <div className={`grid gap-4 ${columnClass}`}>
            {keys.map((key) => (
              <div key={key} className="min-w-0">
                <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                  {formatLabel(key)}
                </div>
                <div className="mt-1">{renderValue(key, data[key])}</div>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm">
      <div className="flex justify-between items-start gap-2">
        <div className="flex-1 min-w-0">
          {renderEntryContent()}
        </div>
        <button
          onClick={onEdit}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-indigo-600 transition-colors"
          title="Edit"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default IPDEntryCard;
